/**
 * 弹窗智能定位工具
 *
 * 计算弹窗的最佳位置，避免超出视口边界
 * 弹窗默认显示在目标位置下方偏右一点的位置
 */

import type { PopupPosition } from '../types/textSelection';

// ==================== 常量定义 ====================

/** 弹窗默认宽度（缩小后约 130px） */
const POPUP_WIDTH = 130;
/** 弹窗默认高度（缩小后约 32px） */
const POPUP_HEIGHT = 32;
/** 弹窗与目标的垂直间距 */
const VERTICAL_OFFSET = 8;
/** 弹窗与目标的水平偏移（稍微向左一点，让第一个按钮对准目标） */
const HORIZONTAL_OFFSET = -40;

// ==================== 定位函数 ====================

/**
 * 计算弹窗的最佳位置
 *
 * @param targetX 目标位置 X 坐标（通常是选中文字的右边缘）
 * @param targetY 目标位置 Y 坐标（通常是选中文字的下边缘）
 * @param viewportWidth 视口宽度
 * @param viewportHeight 视口高度
 * @param popupWidth 弹窗宽度（可选，默认 160）
 * @param popupHeight 弹窗高度（可选，默认 44）
 * @returns 弹窗位置信息
 */
export function calculatePopupPosition(
  targetX: number,
  targetY: number,
  viewportWidth: number = window.innerWidth,
  viewportHeight: number = window.innerHeight,
  popupWidth: number = POPUP_WIDTH,
  popupHeight: number = POPUP_HEIGHT
): PopupPosition {
  // 初始位置：目标位置下方，稍微向左偏移
  let x = targetX + HORIZONTAL_OFFSET;
  let y = targetY + VERTICAL_OFFSET;
  let placement: PopupPosition['placement'] = 'bottom';

  // ===== 水平方向调整 =====

  // 右边界检查：如果弹窗超出右边界
  if (x + popupWidth > viewportWidth - 10) {
    x = viewportWidth - popupWidth - 10;
  }

  // 左边界检查：如果弹窗超出左边界
  if (x < 10) {
    x = 10;
  }

  // ===== 垂直方向调整 =====

  // 下边界检查：如果弹窗下方空间不足，尝试显示在上方
  if (y + popupHeight > viewportHeight - 10) {
    // 计算显示在上方时的位置
    const aboveY = targetY - popupHeight - VERTICAL_OFFSET;

    // 如果上方有足够空间，显示在上方
    if (aboveY >= 10) {
      y = aboveY;
      placement = 'top';
    } else {
      // 上下空间都不够，尽量显示在可见区域内
      y = Math.max(10, Math.min(y, viewportHeight - popupHeight - 10));
    }
  }

  // 上边界检查：确保不会超出视口顶部
  if (y < 10) {
    y = 10;
  }

  return { x, y, placement };
}

/**
 * 检查位置是否在视口内
 * @param x X 坐标
 * @param y Y 坐标
 * @param viewportWidth 视口宽度
 * @param viewportHeight 视口高度
 * @param width 元素宽度
 * @param height 元素高度
 * @returns 是否在视口内
 */
export function isInViewport(
  x: number,
  y: number,
  viewportWidth: number = window.innerWidth,
  viewportHeight: number = window.innerHeight,
  width: number = 0,
  height: number = 0
): boolean {
  return (
    x >= 0 &&
    y >= 0 &&
    x + width <= viewportWidth &&
    y + height <= viewportHeight
  );
}
